type RuntimeEnv = {
  API_BASE_URL?: string;
  API_HOST?: string;
  API_PORT?: string | number;
  API_PROTOCOL?: string;
  USE_MOCK_DATA?: string | boolean;
  SKIP_AUTH_IN_MOCK_MODE?: string | boolean;
  // F8 of epic 2026-05-26_export-flow-and-observability. Base URL of the
  // Grafana instance — used by the admin "User Errors" page to build
  // Tempo trace-view drill-down links from `trace_id_hex`.
  GRAFANA_BASE_URL?: string;
};

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

function getRuntimeValue<T>(key: keyof RuntimeEnv, fallback: T): T {
  if (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__[key] !== undefined) {
    return window.__ENV__[key] as T;
  }
  return fallback;
}

function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return fallback;
}

function getQueryParamOrStorage(key: string, storageKey: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const queryVal = params.get(key);
  
  const isWebView2 = typeof window !== 'undefined' && !!((window as any).chrome && (window as any).chrome.webview);

  if (queryVal !== null) {
    sessionStorage.setItem(storageKey, queryVal);
    if (isWebView2) {
      localStorage.setItem(storageKey, queryVal);
    }
    return queryVal;
  }
  
  if (isWebView2) {
    return sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey);
  }
  return sessionStorage.getItem(storageKey);
}

function buildApiBaseUrl(): string {
  const queryBase = getQueryParamOrStorage('apiBaseUrl', 'pipeex_apiBaseUrl');
  if (queryBase) {
    return queryBase;
  }

  const explicitBase = getRuntimeValue(
    'API_BASE_URL',
    process.env.REACT_APP_PIPEEX_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || ''
  );
  if (explicitBase) {
    return explicitBase;
  }

  const protocol = getRuntimeValue(
    'API_PROTOCOL',
    process.env.REACT_APP_PIPEEX_API_PROTOCOL || process.env.REACT_APP_API_PROTOCOL || 'http'
  );
  const host = getRuntimeValue(
    'API_HOST',
    process.env.REACT_APP_PIPEEX_API_HOST || process.env.REACT_APP_API_HOST || '127.0.0.1'
  );
  const port = getRuntimeValue(
    'API_PORT',
    process.env.REACT_APP_PIPEEX_API_PORT || process.env.REACT_APP_API_PORT || '5381'
  );

  return `${protocol}://${host}:${port}/api`;
}

export const pipeexRuntimeConfig = {
  apiBaseUrl: buildApiBaseUrl(),
  useMockData: (() => {
    const queryMock = getQueryParamOrStorage('useMockData', 'pipeex_useMockData');
    if (queryMock !== null) {
      return asBool(queryMock, true);
    }
    return asBool(
      getRuntimeValue(
        'USE_MOCK_DATA',
        process.env.REACT_APP_PIPEEX_USE_MOCK_DATA ?? process.env.REACT_APP_USE_MOCK_DATA ?? 'true'
      ),
      true
    );
  })(),
  skipAuthInMockMode: asBool(
    getRuntimeValue(
      'SKIP_AUTH_IN_MOCK_MODE',
      process.env.REACT_APP_PIPEEX_SKIP_AUTH_IN_MOCK_MODE ?? 'false'
    ),
    false
  ),
  grafanaBaseUrl: getRuntimeValue(
    'GRAFANA_BASE_URL',
    process.env.REACT_APP_GRAFANA_BASE_URL || ''
  ),
};

